/**
 * <product-info> — owns the variant change of a main-product / featured-product section (SPEC §6).
 *
 * Flow:
 *   1. A variant picker publishes PUB_SUB_EVENTS.optionValueSelectionChange
 *      with { data: { event, target, selectedOptionValues } }.
 *   2. The matching variant is resolved from the embedded product JSON
 *      (selected values may be option value names or option value ids).
 *   3. Immediately: `input[name="id"]` of the section's product forms, the URL (main product only),
 *      the share URL and the media gallery / lightbox are updated.
 *   4. `{product.url}?variant=ID&section_id=SECTION` is fetched and every element carrying
 *      `data-variant-sync` + a stable id is replaced by its counterpart from the response.
 *   5. PUB_SUB_EVENTS.variantChange is published with { data: { sectionId, html, variant } }.
 *
 * Public API: `element.selectVariant(variantId)`, `element.currentVariant`, `element.getVariants()`.
 */
(() => {
  if (window.customElements.get('product-info')) return;

  const pubsubReady = () => typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined';
  const sameId = (a, b) => a != null && b != null && String(a) === String(b);

  class ProductInfo extends HTMLElement {
    constructor() {
      super();
      this.teardown = [];
      this.pendingRequest = null;
      this.lockedButtons = new Map();
    }

    connectedCallback() {
      this.sectionId = this.dataset.section || this.dataset.sectionId;
      this.productUrl = this.dataset.url || '';
      this.shouldUpdateUrl = this.dataset.updateUrl !== 'false';
      this.product = this.readProductData();
      this.currentVariant = this.findVariant(this.dataset.variantId) || null;

      if (pubsubReady()) {
        this.listen();
      } else {
        const retry = () => {
          if (pubsubReady() && !this.teardown.length) this.listen();
        };
        document.addEventListener('DOMContentLoaded', retry, { once: true });
        window.addEventListener('load', retry, { once: true });
      }
    }

    disconnectedCallback() {
      this.teardown.forEach((stop) => {
        if (typeof stop === 'function') stop();
      });
      this.teardown = [];
      if (this.pendingRequest) this.pendingRequest.abort();
    }

    listen() {
      const stop = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, (payload) => this.handleSelection(payload));
      this.teardown.push(stop);
    }

    /* ------------------------------------------------------------------ */
    /* Product data                                                        */
    /* ------------------------------------------------------------------ */
    readProductData() {
      const node = this.querySelector(':scope > script[data-product-data]');
      if (!node) return { options: [], variants: [] };
      try {
        const parsed = JSON.parse(node.textContent);
        return {
          ...parsed,
          options: Array.isArray(parsed.options) ? parsed.options : [],
          variants: Array.isArray(parsed.variants) ? parsed.variants : [],
        };
      } catch (error) {
        return { options: [], variants: [] };
      }
    }

    getVariants() {
      return this.product.variants;
    }

    findVariant(id) {
      if (id == null || id === '') return null;
      return this.product.variants.find((variant) => sameId(variant.id, id)) || null;
    }

    /** Turns a selected value (name, option value id or {id|name|value} object) into the option value name. */
    valueName(raw, position) {
      if (raw == null) return null;
      if (typeof raw === 'object') {
        if (raw.name != null) return String(raw.name);
        if (raw.value != null) return this.valueName(raw.value, position);
        if (raw.id != null) return this.valueName(raw.id, position);
        return null;
      }
      const text = String(raw);
      const option = this.product.options[position];
      if (option && Array.isArray(option.values)) {
        if (option.values.some((value) => value.name === text)) return text;
        const byId = option.values.find((value) => sameId(value.id, text));
        if (byId) return byId.name;
      }
      for (const other of this.product.options) {
        const byId = (other.values || []).find((value) => sameId(value.id, text));
        if (byId) return byId.name;
      }
      return text;
    }

    resolveVariant(data) {
      if (data.variant && data.variant.id != null) return this.findVariant(data.variant.id) || data.variant;
      if (data.variantId != null) return this.findVariant(data.variantId);

      const selected = Array.isArray(data.selectedOptionValues) ? data.selectedOptionValues : [];
      if (!selected.length) return null;
      const names = selected.map((value, index) => this.valueName(value, index));

      return (
        this.product.variants.find((variant) => {
          const options = Array.isArray(variant.options) ? variant.options : [];
          if (options.length !== names.length) return false;
          return options.every((value, index) => value === names[index]);
        }) || null
      );
    }

    /* ------------------------------------------------------------------ */
    /* Selection handling                                                  */
    /* ------------------------------------------------------------------ */
    belongsToMe(node) {
      if (!node || !(node instanceof Element)) return false;
      if (this.contains(node)) return true;
      const host = node.closest('[data-section], [data-section-id]');
      if (host) {
        const id = host.dataset.section || host.dataset.sectionId;
        if (id) return id === this.sectionId;
      }
      const section = document.getElementById(`shopify-section-${this.sectionId}`);
      return !!(section && section.contains(node));
    }

    handleSelection(payload) {
      const data = payload && payload.data ? payload.data : null;
      if (!data) return;
      const origin = data.target || (data.event && data.event.target);
      if (!this.belongsToMe(origin)) return;
      this.applyVariant(this.resolveVariant(data));
    }

    selectVariant(variantId) {
      this.applyVariant(this.findVariant(variantId));
    }

    applyVariant(variant) {
      if (!variant) {
        this.showUnavailable();
        return;
      }
      this.clearUnavailable();

      const changed = !this.currentVariant || !sameId(this.currentVariant.id, variant.id);
      this.currentVariant = variant;
      this.dataset.variantId = variant.id;

      this.writeVariantInputs(variant.id);
      if (this.shouldUpdateUrl) this.writeUrl(variant.id);
      this.writeShareUrl(variant.id);
      this.showVariantMedia(variant);

      if (!changed && this.lastSyncedId && sameId(this.lastSyncedId, variant.id)) return;
      this.renderVariant(variant);
    }

    /* ------------------------------------------------------------------ */
    /* Immediate updates                                                   */
    /* ------------------------------------------------------------------ */
    /** Product forms of this section: ids starting with "product-form" and containing the section id. */
    productForms() {
      const forms = new Set();
      document.querySelectorAll('form[id^="product-form"]').forEach((form) => {
        // getAttribute: `form.id` would return the form's own <input name="id">
        if ((form.getAttribute('id') || '').includes(this.sectionId)) forms.add(form);
      });
      return Array.from(forms);
    }

    writeVariantInputs(variantId) {
      const value = String(variantId);
      this.productForms().forEach((form) => {
        const field = form.elements.namedItem('id');
        if (!field || typeof field.value === 'undefined' || field.value === value) return;
        field.value = value;
        field.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    writeUrl(variantId) {
      if (!this.productUrl || !window.history || typeof window.history.replaceState !== 'function') return;
      const next = new URL(this.productUrl, window.location.origin);
      new URLSearchParams(window.location.search).forEach((value, key) => {
        if (key !== 'variant') next.searchParams.set(key, value);
      });
      next.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', `${next.pathname}${next.search}${window.location.hash}`);
    }

    writeShareUrl(variantId) {
      const url = `${window.location.origin}${this.productUrl}?variant=${variantId}`;
      document.querySelectorAll(`share-button[id^="Share-${this.sectionId}"]`).forEach((share) => {
        if (typeof share.updateUrl === 'function') {
          share.updateUrl(url);
          return;
        }
        const field = share.querySelector('input[id^="ShareUrl-"], input[readonly]');
        if (field) field.value = url;
        if (share.dataset.shareUrl !== undefined) share.dataset.shareUrl = url;
      });
    }

    showVariantMedia(variant) {
      const featured = variant.featured_media;
      document.querySelectorAll(`media-gallery[data-section="${this.sectionId}"]`).forEach((gallery) => {
        const filtering = !!gallery.dataset.filteringOption;
        if (featured && typeof gallery.setActiveMedia === 'function') {
          gallery.setActiveMedia(`${this.sectionId}-${featured.id}`, true, filtering, variant);
        } else if (filtering && typeof gallery.applyVariantFilter === 'function') {
          gallery.applyVariantFilter(variant);
          if (typeof gallery.refresh === 'function') gallery.refresh();
        }
      });

      if (!featured) return;
      const modal = document.getElementById(`ProductModal-${this.sectionId}`);
      const list = modal && modal.querySelector('[role="document"]');
      const item = list && list.querySelector(`:scope > [data-media-id="${featured.id}"]`);
      if (item) list.prepend(item);
    }

    /* ------------------------------------------------------------------ */
    /* Server render                                                       */
    /* ------------------------------------------------------------------ */
    variantSectionUrl(variantId) {
      const url = new URL(this.productUrl || window.location.pathname, window.location.origin);
      url.searchParams.set('variant', variantId);
      url.searchParams.set('section_id', this.sectionId);
      return url.toString();
    }

    renderVariant(variant) {
      if (this.pendingRequest) this.pendingRequest.abort();
      const controller = new AbortController();
      this.pendingRequest = controller;
      const requestedId = variant.id;
      this.classList.add('product-info--loading');

      fetch(this.variantSectionUrl(requestedId), { signal: controller.signal })
        .then((response) => {
          if (!response.ok) throw new Error(`Section request failed (${response.status})`);
          return response.text();
        })
        .then((markup) => {
          if (!this.currentVariant || !sameId(this.currentVariant.id, requestedId)) return;
          const html = new DOMParser().parseFromString(markup, 'text/html');
          this.swapSyncedElements(html);
          this.lastSyncedId = requestedId;
          this.refreshPickup(variant);
          if (pubsubReady() && typeof publish === 'function') {
            publish(PUB_SUB_EVENTS.variantChange, {
              data: { sectionId: this.sectionId, html, variant },
            });
          }
          this.dispatchEvent(
            new CustomEvent('product-info:variant-change', {
              bubbles: true,
              detail: { sectionId: this.sectionId, html, variant },
            })
          );
        })
        .catch((error) => {
          if (error && error.name === 'AbortError') return;
          // keep the immediate updates (form input, URL, media); only server-rendered parts stay stale
          if (window.console) console.warn('[product-info]', error);
        })
        .finally(() => {
          if (this.pendingRequest === controller) {
            this.pendingRequest = null;
            this.classList.remove('product-info--loading');
          }
        });
    }

    swapSyncedElements(html) {
      const focused = document.activeElement;
      const focusedId = focused && focused !== document.body ? focused.id : '';

      html.querySelectorAll('[data-variant-sync][id]').forEach((incoming) => {
        const current = document.getElementById(incoming.id);
        if (!current || !current.hasAttribute('data-variant-sync')) return;
        if (current.outerHTML === incoming.outerHTML) return;
        current.replaceWith(document.importNode(incoming, true));
      });

      if (focusedId && document.activeElement !== focused) {
        const again = document.getElementById(focusedId);
        if (again && typeof again.focus === 'function') again.focus({ preventScroll: true });
      }
    }

    refreshPickup(variant) {
      const section = document.getElementById(`shopify-section-${this.sectionId}`) || this;
      const pickup = section.querySelector('pickup-availability');
      if (!pickup) return;
      if (variant.available) {
        if (!sameId(pickup.dataset.variantId, variant.id) && typeof pickup.fetchAvailability === 'function') {
          pickup.fetchAvailability(variant.id);
        }
      } else {
        pickup.removeAttribute('available');
        pickup.innerHTML = '';
      }
    }

    /* ------------------------------------------------------------------ */
    /* Unavailable option combination                                      */
    /* ------------------------------------------------------------------ */
    addButtons() {
      const buttons = [];
      this.productForms().forEach((form) => {
        form.querySelectorAll('[name="add"]').forEach((button) => buttons.push(button));
      });
      return buttons;
    }

    showUnavailable() {
      this.currentVariant = null;
      this.lastSyncedId = null;
      if (this.pendingRequest) this.pendingRequest.abort();
      this.classList.add('product-info--unavailable');
      const label = (window.variantStrings && window.variantStrings.unavailable) || this.dataset.unavailableLabel || '';
      // the product form script (PB1) manages its own buttons when it is present
      const formManagesButtons = !!(window.NuvoroProductForm && typeof window.NuvoroProductForm.applyState === 'function');

      (formManagesButtons ? [] : this.addButtons()).forEach((button) => {
        if (!this.lockedButtons.has(button)) {
          const textNode = button.querySelector('.main-atc__label, [data-add-label], span:not([class*="spinner"]):not(.visually-hidden)');
          this.lockedButtons.set(button, {
            disabled: button.disabled,
            textNode,
            text: textNode ? textNode.textContent : null,
          });
        }
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        const saved = this.lockedButtons.get(button);
        if (saved.textNode && label) saved.textNode.textContent = label;
      });

      document.querySelectorAll('[data-variant-sync][id]').forEach((node) => {
        if (node.id.includes(this.sectionId) && /^(price|sku|inventory)/i.test(node.id)) {
          node.classList.add('visibility-hidden');
        }
      });

      this.dispatchEvent(new CustomEvent('product-info:unavailable', { bubbles: true, detail: { sectionId: this.sectionId } }));
    }

    clearUnavailable() {
      if (!this.classList.contains('product-info--unavailable')) return;
      this.classList.remove('product-info--unavailable');
      this.lockedButtons.forEach((saved, button) => {
        if (!button.isConnected) return;
        button.disabled = saved.disabled;
        if (!saved.disabled) button.removeAttribute('aria-disabled');
        if (saved.textNode && saved.text != null) saved.textNode.textContent = saved.text;
      });
      this.lockedButtons.clear();
      document.querySelectorAll('[data-variant-sync][id].visibility-hidden').forEach((node) => {
        if (node.id.includes(this.sectionId) && /^(price|sku|inventory)/i.test(node.id)) {
          node.classList.remove('visibility-hidden');
        }
      });
    }
  }

  window.customElements.define('product-info', ProductInfo);
})();
